import {FormField, Separator, type FormControl} from 'platform/components';
import {Box, Grid, Heading, Text, VStack} from 'platform/foundation';

import {useWatch, type UseFormReturn} from 'react-hook-form';
import {Trans} from 'react-i18next';

import i18n from '@dms/i18n';
import {useTenant} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {type CampaignFormValues} from '../campaignFormSchema';
import {useCampaignFilterOptions} from '../hooks/useCampaignFilterOptions';
import {CampaignManuallySelectedVehicles} from './CampaignManuallySelectedVehicles';

const VEHICLE_TYPE_DEPENDENT_FIELDS = [
  'makes',
  'modelFamilies',
  'models',
  'bodyStyles',
  'fuelTypes',
  'vehicleConditions',
] as const;

const MAKES_DEPENDENT_FIELDS = ['modelFamilies', 'models'] as const;

interface CampaignVehicleSelectionSectionProps extends RequiredTestIdProps {
  control: FormControl<CampaignFormValues>;
  formApi: UseFormReturn<CampaignFormValues>;
}

export function CampaignVehicleSelectionSection(props: CampaignVehicleSelectionSectionProps) {
  const {tenantCurrency} = useTenant();

  const mileageSuffix = i18n.t('general.metric.km');
  const daysSuffix = i18n.t('general.metric.d');

  const [vehicleType, selectedMakes, isAppliedToAllVehicles, manuallySelectedVehicles] = useWatch({
    control: props.control,
    name: [
      'filter.vehicleType',
      'filter.makes',
      'isAppliedToAllVehicles',
      'manuallySelectedVehicles',
    ],
  });

  const filterOptions = useCampaignFilterOptions(vehicleType, selectedMakes);

  const onVehicleTypeChange = () =>
    VEHICLE_TYPE_DEPENDENT_FIELDS.forEach((field) => props.formApi.setValue(`filter.${field}`, []));

  const onMakesChange = () =>
    MAKES_DEPENDENT_FIELDS.forEach((field) => props.formApi.setValue(`filter.${field}`, []));

  const isVehicleTypeDependentDisabled =
    isAppliedToAllVehicles || filterOptions.isMissingVehicleType;

  const isMakeDependentDisabled = isAppliedToAllVehicles || filterOptions.isMissingMakes;

  const selectVehicleTypeTooltip =
    !isAppliedToAllVehicles && filterOptions.isMissingVehicleType
      ? i18n.t('entity.interest.tooltips.selectMake')
      : undefined;
  const selectMakeTooltip =
    !isAppliedToAllVehicles && filterOptions.isMissingMakes
      ? i18n.t('entity.interest.tooltips.selectModel')
      : undefined;

  return (
    <VStack spacing={6}>
      <Heading size={4}>{i18n.t('entity.salesCampaign.sections.vehicleSelection')}</Heading>

      <Box
        padding={4}
        borderRadius="medium"
        border="1px solid"
        borderColor="general.separator"
        data-testid={suffixTestId('filter', props)}
      >
        <VStack spacing={4}>
          <FormField
            control={props.control}
            type="checkbox"
            name="isAppliedToAllVehicles"
            label={i18n.t('entity.salesCampaign.labels.applyToAllVehicles')}
            helperText={i18n.t('entity.salesCampaign.hints.applyToAllVehicles')}
            data-testid={suffixTestId('filter-applyToAllVehicles', props)}
          />

          <Separator spacing={0} />

          <VStack spacing={1}>
            <Heading size={5}>{i18n.t('entity.interest.labels.conditions')}</Heading>
            <Text size="xSmall" color="tertiary">
              <Trans
                i18nKey="entity.salesCampaign.sections.conditionsDescription"
                components={{break: <br />}}
              />
            </Text>
          </VStack>

          <Grid columns={2} spacing={4}>
            <FormField
              control={props.control}
              type="multiChoice"
              name="filter.branchIds"
              label={i18n.t('entity.branch.labels.branch')}
              options={filterOptions.branchOptions}
              isLoading={filterOptions.isBranchesLoading}
              isDisabled={isAppliedToAllVehicles}
              placeholder={i18n.t('general.labels.selectOption')}
              menuInPortal
              data-testid={suffixTestId('filter-branchIds', props)}
            />
            <FormField
              control={props.control}
              type="multiChoice"
              name="filter.salePresetIds"
              label={i18n.t('entity.vehicle.labels.salePreset')}
              options={filterOptions.salePresetOptions}
              isLoading={filterOptions.isSalePresetsLoading}
              isDisabled={isAppliedToAllVehicles}
              placeholder={i18n.t('general.labels.selectOption')}
              menuInPortal
              data-testid={suffixTestId('filter-salePresetIds', props)}
            />

            <FormField
              control={props.control}
              type="choice"
              name="filter.vehicleType"
              label={i18n.t('entity.vehicle.labels.type')}
              options={filterOptions.vehicleTypeOptions}
              isLoading={filterOptions.isCatalogueLoading}
              isDisabled={isAppliedToAllVehicles}
              placeholder={i18n.t('general.labels.selectOption')}
              menuInPortal
              onChange={onVehicleTypeChange}
              data-testid={suffixTestId('filter-vehicleType', props)}
            />
            <FormField
              control={props.control}
              type="multiChoice"
              name="filter.vehicleConditions"
              label={i18n.t('entity.vehicle.labels.condition')}
              options={filterOptions.vehicleConditionOptions}
              isLoading={filterOptions.isCatalogueLoading}
              isDisabled={isVehicleTypeDependentDisabled}
              tooltip={selectVehicleTypeTooltip}
              placeholder={i18n.t('general.labels.selectOption')}
              menuInPortal
              data-testid={suffixTestId('filter-vehicleConditions', props)}
            />

            <FormField
              control={props.control}
              type="multiChoice"
              name="filter.makes"
              label={i18n.t('entity.vehicle.labels.make')}
              options={filterOptions.makeOptions}
              isLoading={filterOptions.isMakeModelLoading}
              isDisabled={isVehicleTypeDependentDisabled}
              tooltip={selectVehicleTypeTooltip}
              placeholder={i18n.t('general.labels.selectOption')}
              menuInPortal
              onChange={onMakesChange}
              data-testid={suffixTestId('filter-makes', props)}
            />
            <FormField
              control={props.control}
              type="multiChoice"
              name="filter.models"
              label={i18n.t('entity.vehicle.labels.model')}
              options={filterOptions.modelOptionsGroupedByMake}
              isLoading={filterOptions.isMakeModelLoading}
              isDisabled={isMakeDependentDisabled}
              tooltip={selectMakeTooltip}
              placeholder={i18n.t('general.labels.selectOption')}
              menuInPortal
              data-testid={suffixTestId('filter-models', props)}
            />

            <FormField
              control={props.control}
              type="multiChoice"
              name="filter.modelFamilies"
              label={i18n.t('entity.vehicle.labels.modelFamily')}
              options={filterOptions.modelFamilyOptionsGroupedByMake}
              isLoading={filterOptions.isMakeModelLoading}
              isDisabled={isMakeDependentDisabled}
              tooltip={selectMakeTooltip}
              placeholder={i18n.t('general.labels.selectOption')}
              menuInPortal
              data-testid={suffixTestId('filter-modelFamilies', props)}
            />
            <FormField
              control={props.control}
              type="multiChoice"
              name="filter.fuelTypes"
              label={i18n.t('entity.vehicle.labels.fuel')}
              options={filterOptions.fuelTypeOptions}
              isLoading={filterOptions.isCatalogueLoading}
              isDisabled={isVehicleTypeDependentDisabled}
              tooltip={selectVehicleTypeTooltip}
              placeholder={i18n.t('general.labels.selectOption')}
              menuInPortal
              data-testid={suffixTestId('filter-fuelTypes', props)}
            />

            <FormField
              control={props.control}
              type="multiChoice"
              name="filter.bodyStyles"
              label={i18n.t('entity.vehicle.labels.carStyle')}
              options={filterOptions.bodyStyleOptions}
              isLoading={filterOptions.isCatalogueLoading}
              isDisabled={isVehicleTypeDependentDisabled}
              tooltip={selectVehicleTypeTooltip}
              placeholder={i18n.t('general.labels.selectOption')}
              menuInPortal
              data-testid={suffixTestId('filter-bodyStyles', props)}
            />
            <FormField
              control={props.control}
              type="multiChoice"
              name="filter.tagIds"
              label={i18n.t('entity.salesCampaign.labels.vehicleTag')}
              options={filterOptions.tagOptions}
              isLoading={filterOptions.isTagsLoading}
              isDisabled={isAppliedToAllVehicles}
              placeholder={i18n.t('general.labels.selectOption')}
              menuInPortal
              data-testid={suffixTestId('filter-tagIds', props)}
            />

            <FormField
              control={props.control}
              type="number"
              name="filter.priceFrom"
              label={i18n.t('entity.salesCampaign.labels.priceFrom')}
              suffix={tenantCurrency}
              isDisabled={isAppliedToAllVehicles}
              data-testid={suffixTestId('filter-priceFrom', props)}
            />
            <FormField
              control={props.control}
              type="number"
              name="filter.priceTo"
              label={i18n.t('entity.salesCampaign.labels.priceTo')}
              suffix={tenantCurrency}
              isDisabled={isAppliedToAllVehicles}
              data-testid={suffixTestId('filter-priceTo', props)}
            />

            <FormField
              control={props.control}
              type="integer"
              name="filter.mileageFrom"
              label={i18n.t('entity.vehicle.labels.mileageFrom')}
              suffix={mileageSuffix}
              isDisabled={isAppliedToAllVehicles}
              data-testid={suffixTestId('filter-mileageFrom', props)}
            />
            <FormField
              control={props.control}
              type="integer"
              name="filter.mileageTo"
              label={i18n.t('entity.vehicle.labels.mileageTo')}
              suffix={mileageSuffix}
              isDisabled={isAppliedToAllVehicles}
              data-testid={suffixTestId('filter-mileageTo', props)}
            />

            <FormField
              control={props.control}
              type="integer"
              name="filter.yearFrom"
              label={i18n.t('entity.salesCampaign.labels.yearFrom')}
              isDisabled={isAppliedToAllVehicles}
              data-testid={suffixTestId('filter-yearFrom', props)}
            />
            <FormField
              control={props.control}
              type="integer"
              name="filter.yearTo"
              label={i18n.t('entity.salesCampaign.labels.yearTo')}
              isDisabled={isAppliedToAllVehicles}
              data-testid={suffixTestId('filter-yearTo', props)}
            />

            <FormField
              control={props.control}
              type="integer"
              name="filter.daysInStock"
              label={i18n.t('entity.salesCampaign.labels.stockAgeDays')}
              helperText={i18n.t('entity.salesCampaign.hints.stockAgeDays')}
              suffix={daysSuffix}
              isDisabled={isAppliedToAllVehicles}
              data-testid={suffixTestId('filter-daysInStock', props)}
            />
            <FormField
              control={props.control}
              type="apiDate"
              name="filter.stockInDateFrom"
              label={i18n.t('entity.salesCampaign.labels.stockInDateFrom')}
              isDisabled={isAppliedToAllVehicles}
              data-testid={suffixTestId('filter-stockInDateFrom', props)}
            />
          </Grid>
        </VStack>
      </Box>
      <CampaignManuallySelectedVehicles
        value={manuallySelectedVehicles}
        onChange={(next) =>
          props.formApi.setValue('manuallySelectedVehicles', next, {shouldDirty: true})
        }
        data-testid={suffixTestId('manuallySelectedVehicles', props)}
      />
    </VStack>
  );
}
